import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search,
  Users,
  Mail,
  BarChart3,
  Settings,
  TrendingUp,
  TrendingDown,
  CalendarClock,
  Download,
  LogOut,
  Loader2,
  Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import { IuniFlower } from "@/components/landing/IuniFlower";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_LABELS = { new: "Nuovo", contacted: "Contattato", invited: "Invitato" };
const STATUS_STYLES = {
  new: "bg-[rgba(224,122,95,0.1)] text-[#C2492F]",
  contacted: "bg-[#FFF3E0] text-[#B25E09]",
  invited: "bg-[#E7F6EC] text-[#1E7B34]",
};

const NAV = [
  { label: "Waitlist & Leads", icon: Users, active: true },
  { label: "Email Campaigns", icon: Mail, active: false },
  { label: "Analytics", icon: BarChart3, active: false },
  { label: "Settings", icon: Settings, active: false },
];

function monthsUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  return (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth());
}

function timelineInfo(dateStr) {
  const m = monthsUntil(dateStr);
  if (m === null) return { label: "Da definire", cls: "bg-gray-100 text-gray-500" };
  if (m < 0) return { label: "Passato", cls: "bg-gray-100 text-gray-400" };
  if (m === 0) return { label: "Questo mese", cls: "bg-[#FDE8E4] text-[#C2492F]" };
  const label = `Tra ${m} ${m === 1 ? "mese" : "mesi"}`;
  if (m <= 6) return { label, cls: "bg-[#FDE8E4] text-[#C2492F]" };
  if (m <= 12) return { label, cls: "bg-[#FFF3E0] text-[#B25E09]" };
  return { label, cls: "bg-[#E7F6EC] text-[#1E7B34]" };
}

const fmtDate = (iso, opts) =>
  new Intl.DateTimeFormat("it-IT", opts || { day: "numeric", month: "short", year: "numeric" }).format(
    new Date(iso)
  );

function EngagementRing({ pct }) {
  return (
    <div className="relative w-14 h-14">
      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
        <defs>
          <linearGradient id="admin-ring-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#E07A5F" />
            <stop offset="100%" stopColor="#E2856E" />
          </linearGradient>
        </defs>
        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#F1F1F4" strokeWidth="3.5" />
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          stroke="url(#admin-ring-gradient)"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={`${pct} ${100 - pct}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#2D2926]">
        {pct}%
      </span>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const loginWithGoogle = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/password", { password });
      onLogin(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Accesso non riuscito. Riprova.");
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F5F5F7] flex items-center justify-center px-6"
      data-testid="admin-login"
    >
      <div className="bg-white rounded-3xl shadow-[0_30px_80px_-24px_rgba(0,0,0,0.12)] border border-gray-100/60 p-10 sm:p-12 max-w-sm w-full text-center">
        <div className="flex justify-center">
          <IuniFlower size={40} />
        </div>
        <p className="text-2xl font-bold tracking-tight text-[#2D2926] mt-4">iuni</p>
        <p className="text-sm text-gray-400 mt-2">
          Area riservata ai fondatori. Accedi per gestire la waitlist.
        </p>
        <button
          data-testid="admin-google-login-button"
          onClick={loginWithGoogle}
          className="mt-8 w-full inline-flex items-center justify-center gap-2.5 px-5 py-3.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-[#E07A5F] to-[#E2856E] shadow-[0_4px_14px_rgba(224,122,95,0.39)] hover:shadow-[0_6px_20px_rgba(224,122,95,0.3)] hover:-translate-y-0.5 transition-[box-shadow,transform]"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z" />
          </svg>
          Accedi con Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-gray-100" />
          <span className="text-[10px] font-semibold tracking-widest text-gray-300 uppercase">
            oppure
          </span>
          <span className="flex-1 h-px bg-gray-100" />
        </div>

        <form onSubmit={submitPassword} data-testid="admin-password-form">
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password di accesso"
            className="w-full bg-gray-50/60 border border-gray-200 rounded-xl px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/60 focus:border-transparent transition-[box-shadow]"
          />
          {error && (
            <p className="text-xs text-[#C2492F] mt-2" data-testid="admin-password-error">
              {error}
            </p>
          )}
          <button
            data-testid="admin-password-submit"
            type="submit"
            disabled={loading || !password}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-60 disabled:pointer-events-none"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            Accedi con password
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    // CRITICAL: if returning from the OAuth callback, AuthCallback handles the exchange first.
    if (window.location.hash?.includes("session_id=")) return;
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        setAuthChecked(true);
      })
      .catch(() => setAuthChecked(true));
  }, [location.state]);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/waitlist"), api.get("/waitlist/stats")])
      .then(([leadsRes, statsRes]) => {
        setLeads(leadsRes.data);
        setStats(statsRes.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setUser(null);
        } else {
          toast.error("Impossibile caricare i dati. Riprova.");
        }
      })
      .finally(() => setLoading(false));
  }, [user]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      toast.success("Disconnesso.");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) => l.couple_names.toLowerCase().includes(q) || l.email.toLowerCase().includes(q)
    );
  }, [leads, search]);

  const updateStatus = async (lead, status) => {
    try {
      const res = await api.patch(`/waitlist/${lead.id}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? res.data : l)));
      toast.success(`Stato aggiornato: ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("Aggiornamento non riuscito. Riprova.");
    }
  };

  const deleteLead = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/waitlist/${deleteTarget.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success("Contatto eliminato con successo");
      api.get("/waitlist/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch {
      toast.error("Eliminazione non riuscita. Riprova.");
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = () => {
    const headers = ["Coppia", "Email", "Data Nozze", "Stress Point", "Iscrizione", "Stato"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((l) => [
      l.couple_names,
      l.email,
      l.wedding_date ? fmtDate(l.wedding_date) : "",
      l.stress_point || "",
      fmtDate(l.created_at),
      STATUS_LABELS[l.status] || l.status,
    ]);
    const csv = "﻿" + [headers, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `iuni-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportate ${filtered.length} iscrizioni in CSV.`);
  };

  const today = new Intl.DateTimeFormat("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  const trend = stats?.trend_pct ?? 0;

  if (!authChecked) {
    return (
      <div
        className="min-h-screen bg-[#F5F5F7] flex items-center justify-center"
        data-testid="admin-loading"
      >
        <Loader2 className="animate-spin text-[#E07A5F]" size={28} />
      </div>
    );
  }
  if (!user) return <AdminLogin onLogin={(u) => { setUser(u); setAuthChecked(true); }} />;

  const initials = (user.name || user.email || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[#F5F5F7] text-[#1A1A1A]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-gray-200/60 px-5 py-7 z-30">
        <div className="flex items-center gap-3 px-2">
          <IuniFlower size={28} />
          <p className="text-xl font-bold tracking-tight text-[#2D2926]">iuni</p>
          <span className="text-[10px] font-semibold tracking-widest text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            ADMIN
          </span>
        </div>
        <nav className="mt-10 space-y-1.5">
          {NAV.map((item) => (
            <button
              key={item.label}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${
                item.active
                  ? "bg-[rgba(224,122,95,0.08)] text-[#C2492F] font-semibold"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              }`}
            >
              <item.icon size={17} strokeWidth={item.active ? 2.2 : 1.8} />
              {item.label}
              {!item.active && (
                <span className="ml-auto text-[9px] font-semibold tracking-widest text-gray-300 uppercase">
                  Presto
                </span>
              )}
            </button>
          ))}
        </nav>
        <p className="mt-auto px-4 text-[11px] text-gray-300">Beta privata · Solo 50 coppie</p>
      </aside>

      {/* Main column */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
          <div className="px-6 sm:px-8 h-20 flex items-center gap-4">
            <div className="flex lg:hidden items-center gap-2 mr-1">
              <IuniFlower size={24} />
              <span className="font-bold text-[#2D2926]">iuni</span>
              <span className="text-[9px] font-semibold tracking-widest text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                ADMIN
              </span>
            </div>
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca coppia o email..."
                className="w-full bg-gray-50/80 border border-gray-200 rounded-xl pl-11 pr-4 py-2.5 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/60 focus:border-transparent transition-[box-shadow]"
              />
            </div>
            <p className="hidden md:block text-sm text-gray-400 capitalize ml-auto" data-testid="admin-current-date">
              {today}
            </p>
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10" data-testid="admin-profile-avatar">
                <AvatarImage src={user.picture} alt={user.name || "Founder"} />
                <AvatarFallback className="bg-gradient-to-br from-[#E07A5F] to-[#E2856E] text-white text-xs font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <button
                data-testid="admin-logout-button"
                onClick={logout}
                title="Esci"
                className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-[#C2492F] hover:bg-[rgba(224,122,95,0.08)] transition-colors"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>

        <main className="px-6 sm:px-8 py-10 max-w-6xl">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#2D2926]">
            Waitlist &amp; Leads
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Gestisci le iscrizioni alla Beta e monitora le timeline dei matrimoni.
          </p>

          {/* KPI cards */}
          <div className="grid sm:grid-cols-3 gap-5 mt-8">
            <div
              data-testid="admin-kpi-total"
              className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/60 p-6"
            >
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
                Totale Iscritti
              </p>
              <div className="flex items-end justify-between mt-3">
                <p className="text-4xl font-bold text-[#2D2926]">{stats ? stats.total : "—"}</p>
                <span
                  className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                    trend >= 0 ? "bg-[#E7F6EC] text-[#1E7B34]" : "bg-[#FDE8E4] text-[#C2492F]"
                  }`}
                >
                  {trend >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                  {trend >= 0 ? "+" : ""}
                  {trend}%
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-3">
                Ultimi 7 giorni: +{stats?.signups_last_7_days ?? 0}
              </p>
            </div>
            <div
              data-testid="admin-kpi-imminent"
              className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/60 p-6"
            >
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
                Matrimoni Imminenti
              </p>
              <div className="flex items-end justify-between mt-3">
                <p className="text-4xl font-bold text-[#2D2926]">{stats ? stats.imminent : "—"}</p>
                <span className="w-9 h-9 rounded-xl bg-[rgba(224,122,95,0.1)] flex items-center justify-center">
                  <CalendarClock size={17} className="text-[#E07A5F]" />
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-3">Entro i prossimi 6 mesi</p>
            </div>
            <div
              data-testid="admin-kpi-engagement"
              className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/60 p-6"
            >
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
                Tasso di Attività
              </p>
              <div className="flex items-center justify-between mt-2">
                <div>
                  <p className="text-4xl font-bold text-[#2D2926]">
                    {stats ? `${stats.engagement}%` : "—"}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-3">Profili completi di data e priorità</p>
                </div>
                <EngagementRing pct={stats?.engagement ?? 0} />
              </div>
            </div>
          </div>

          {/* Leads table */}
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/60 mt-8 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-[#2D2926]">Iscrizioni alla waitlist</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400" data-testid="admin-leads-count">
                  {filtered.length} {filtered.length === 1 ? "coppia" : "coppie"}
                </span>
                <button
                  data-testid="admin-export-csv-button"
                  onClick={exportCsv}
                  disabled={loading || filtered.length === 0}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-white px-4 py-2 rounded-xl bg-gradient-to-r from-[#E07A5F] to-[#E2856E] shadow-[0_4px_14px_rgba(224,122,95,0.39)] hover:shadow-[0_6px_20px_rgba(224,122,95,0.3)] hover:-translate-y-0.5 transition-[box-shadow,transform] disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Download size={13} />
                  Scarica Excel (CSV)
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="admin-leads-table">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-6 text-[11px] uppercase tracking-widest text-gray-400">
                      Nomi della coppia
                    </TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-gray-400">
                      Email
                    </TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-gray-400">
                      Data del matrimonio
                    </TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-gray-400">
                      Cosa li stressa di più
                    </TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-gray-400">
                      Data di iscrizione
                    </TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-gray-400">
                      Stato
                    </TableHead>
                    <TableHead className="pr-6 text-[11px] uppercase tracking-widest text-gray-400">
                      <span className="sr-only">Azioni</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-16 text-center text-sm text-gray-400">
                        Caricamento iscrizioni...
                      </TableCell>
                    </TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-16 text-center text-sm text-gray-400"
                        data-testid="admin-leads-empty"
                      >
                        Nessuna iscrizione trovata.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((lead) => {
                      const tl = timelineInfo(lead.wedding_date);
                      return (
                        <TableRow
                          key={lead.id}
                          data-testid={`admin-lead-row-${lead.id}`}
                          className="hover:bg-[#FBFBFB]"
                        >
                          <TableCell className="pl-6 py-4">
                            <p className="text-sm font-semibold text-[#2D2926]">{lead.couple_names}</p>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">{lead.email}</TableCell>
                          <TableCell>
                            <p className="text-sm text-gray-600">
                              {lead.wedding_date ? fmtDate(lead.wedding_date) : "—"}
                            </p>
                            <span
                              className={`inline-block mt-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${tl.cls}`}
                              data-testid={`admin-lead-timeline-${lead.id}`}
                            >
                              {tl.label}
                            </span>
                          </TableCell>
                          <TableCell>
                            {lead.stress_point ? (
                              <span className="text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                {lead.stress_point}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {fmtDate(lead.created_at)}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={lead.status}
                              onValueChange={(v) => updateStatus(lead, v)}
                            >
                              <SelectTrigger
                                data-testid={`admin-lead-status-${lead.id}`}
                                className={`w-[8.5rem] h-8 text-xs font-semibold border-0 rounded-full ${STATUS_STYLES[lead.status]}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                  <SelectItem key={value} value={value} className="text-xs">
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="pr-6">
                            <button
                              data-testid={`admin-lead-delete-${lead.id}`}
                              onClick={() => setDeleteTarget(lead)}
                              title="Elimina"
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-[#C2492F] hover:bg-[rgba(224,122,95,0.08)] transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </main>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/20 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
            data-testid="admin-delete-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-7"
            >
              <span className="w-11 h-11 rounded-2xl bg-[#FDE8E4] flex items-center justify-center">
                <Trash2 size={18} className="text-[#C2492F]" />
              </span>
              <h3 className="text-lg font-bold text-gray-900 mt-4">Conferma eliminazione</h3>
              <p className="text-sm text-gray-600 leading-relaxed mt-2">
                Sei sicuro di voler eliminare questo iscritto? Questa azione è irreversibile e il
                contatto verrà rimosso definitivamente dal database.
              </p>
              <p className="text-xs text-gray-400 mt-3 bg-gray-50 rounded-lg px-3 py-2">
                {deleteTarget.couple_names} · {deleteTarget.email}
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  data-testid="admin-delete-cancel-button"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-60"
                >
                  Annulla
                </button>
                <button
                  data-testid="admin-delete-confirm-button"
                  onClick={deleteLead}
                  disabled={deleting}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#C2492F] hover:bg-[#a93d27] rounded-xl transition-colors disabled:opacity-60 disabled:pointer-events-none"
                >
                  {deleting && <Loader2 size={14} className="animate-spin" />}
                  Elimina definitivamente
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
